import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  Copy,
  Loader2,
  PlugZap,
  RefreshCw,
  Send,
  ShieldCheck,
} from "lucide-react";
import {
  createWebhookSubscription,
  listWebhookDeliveries,
  listWebhookSubscriptions,
  replayWebhookDelivery,
  setComplianceContext,
  testWebhookSubscription,
  updateWebhookSubscriptionStatus,
} from "../../services/compliance-api";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import type {
  WebhookDeliveryItem,
  WebhookSubscriptionItem,
} from "../../types/compliance";

const DEFAULT_EVENTS = [
  "document.uploaded",
  "document.amended",
  "v2.registry_checked",
  "v2.consistency_auto_checked",
  "v2.sanctions_auto_screened",
  "integration.test",
];

const STATUS_STYLES: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  paused: "bg-yellow-100 text-yellow-800",
  revoked: "bg-gray-100 text-gray-600",
  success: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-700",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
        STATUS_STYLES[status] ?? "bg-blue-100 text-blue-700"
      }`}
    >
      {status}
    </span>
  );
}

function MetricCard({
  label,
  value,
  helper,
}: {
  label: string;
  value: string | number;
  helper: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <p className="mt-2 text-2xl font-bold text-gray-900">{value}</p>
      <p className="mt-1 text-xs text-gray-500">{helper}</p>
    </div>
  );
}

export default function WebhookIntegrationConsole() {
  const { credentials } = useComplianceAuth();
  const tenantId = credentials?.tenantId ?? "default";
  const role = credentials?.role ?? "COMPLIANCE_ADMIN";
  const isAdmin = role === "COMPLIANCE_ADMIN";

  const [subscriptions, setSubscriptions] = useState<WebhookSubscriptionItem[]>([]);
  const [deliveries, setDeliveries] = useState<WebhookDeliveryItem[]>([]);
  const [targetUrl, setTargetUrl] = useState("");
  const [selectedEvents, setSelectedEvents] = useState<string[]>(DEFAULT_EVENTS);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [replayingId, setReplayingId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState<"all" | "success" | "failed">("all");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const metrics = useMemo(() => {
    const active = subscriptions.filter((item) => item.status === "active").length;
    const failed = deliveries.filter((item) => item.status === "failed").length;
    const success = deliveries.filter((item) => item.status === "success").length;
    return { active, failed, success };
  }, [subscriptions, deliveries]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setComplianceContext(tenantId, role);
      const [subscriptionRes, deliveryRes] = await Promise.all([
        listWebhookSubscriptions(),
        listWebhookDeliveries({
          status: deliveryStatusFilter === "all" ? undefined : deliveryStatusFilter,
        }),
      ]);
      setSubscriptions(subscriptionRes.items);
      setDeliveries(deliveryRes.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load webhook integrations");
    } finally {
      setLoading(false);
    }
  }, [tenantId, role, deliveryStatusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  function toggleEvent(eventName: string) {
    setSelectedEvents((prev) =>
      prev.includes(eventName)
        ? prev.filter((item) => item !== eventName)
        : [...prev, eventName],
    );
  }

  async function handleCreate() {
    if (!targetUrl.trim()) {
      setError("Webhook target URL is required.");
      return;
    }
    setSaving(true);
    setError(null);
    setNewSecret(null);
    try {
      const response = await createWebhookSubscription({
        target_url: targetUrl.trim(),
        event_types: selectedEvents,
      });
      setSubscriptions((prev) => [response.subscription, ...prev]);
      setTargetUrl("");
      setNewSecret(response.signing_secret);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create webhook subscription");
    } finally {
      setSaving(false);
    }
  }

  async function handleStatus(subscriptionId: string, status: "active" | "paused" | "revoked") {
    setUpdatingId(subscriptionId);
    setError(null);
    try {
      const updated = await updateWebhookSubscriptionStatus(subscriptionId, status);
      setSubscriptions((prev) =>
        prev.map((item) => (item.subscription_id === subscriptionId ? updated : item)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update webhook status");
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleTest(subscriptionId: string) {
    setTestingId(subscriptionId);
    setError(null);
    try {
      const response = await testWebhookSubscription(subscriptionId, {
        payload: { source: "webhook_console", tenant_id: tenantId },
      });
      setDeliveries((prev) => [...response.items, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send webhook test event");
    } finally {
      setTestingId(null);
    }
  }

  async function handleReplay(deliveryId: string) {
    setReplayingId(deliveryId);
    setError(null);
    try {
      const replayed = await replayWebhookDelivery(deliveryId, tenantId);
      setDeliveries((prev) => [replayed, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to replay webhook delivery");
    } finally {
      setReplayingId(null);
    }
  }

  async function copySecret() {
    if (!newSecret) return;
    await navigator.clipboard.writeText(newSecret);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={28} />
        <span className="ml-3 text-gray-600">Loading webhook integrations...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <PlugZap className="text-blue-600" size={28} />
            ERP Webhook Integrations
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            Connect LightLink compliance events to ERP, customs broker, WMS, TMS, or audit systems with signed webhook delivery.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle size={15} className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <MetricCard label="Active Hooks" value={metrics.active} helper="Subscriptions currently receiving events." />
        <MetricCard label="Successful Deliveries" value={metrics.success} helper="Recent signed POSTs accepted by targets." />
        <MetricCard label="Failed Deliveries" value={metrics.failed} helper="Failures requiring ERP endpoint review." />
      </div>

      {isAdmin && (
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-5">
          <div className="mb-4 flex items-center gap-2">
            <ShieldCheck size={18} className="text-blue-700" />
            <h2 className="font-semibold text-blue-950">Create production webhook</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_1.4fr_auto] xl:items-end">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Target URL
              </label>
              <input
                type="url"
                value={targetUrl}
                onChange={(event) => setTargetUrl(event.target.value)}
                placeholder="https://erp.company.com/linklight/webhook"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <p className="mb-2 text-xs font-medium text-gray-700">Events</p>
              <div className="flex flex-wrap gap-2">
                {DEFAULT_EVENTS.map((eventName) => (
                  <button
                    key={eventName}
                    type="button"
                    onClick={() => toggleEvent(eventName)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium ${
                      selectedEvents.includes(eventName)
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {eventName}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={saving}
              className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-gray-400"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <PlugZap size={15} />}
              Create
            </button>
          </div>
          {newSecret && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                Copy signing secret now
              </p>
              <div className="mt-2 flex flex-col gap-2 md:flex-row md:items-center">
                <code className="flex-1 break-all rounded bg-white px-3 py-2 text-xs text-gray-800">
                  {newSecret}
                </code>
                <button
                  type="button"
                  onClick={() => void copySecret()}
                  className="flex items-center justify-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100"
                >
                  {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mb-6 rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-4 py-3">
          <h2 className="font-semibold text-gray-900">Subscriptions</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Endpoint</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Events</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {subscriptions.map((subscription) => (
                <tr key={subscription.subscription_id} className="hover:bg-gray-50">
                  <td className="max-w-sm px-4 py-3">
                    <p className="break-all text-sm font-medium text-gray-900">{subscription.target_url}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      {subscription.subscription_id} · Updated {new Date(subscription.updated_at).toLocaleString()}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex max-w-lg flex-wrap gap-1">
                      {subscription.event_types.map((eventName) => (
                        <span key={eventName} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          {eventName}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={subscription.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => void handleTest(subscription.subscription_id)}
                        disabled={testingId === subscription.subscription_id || subscription.status !== "active"}
                        className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {testingId === subscription.subscription_id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                        Test
                      </button>
                      {isAdmin && subscription.status === "active" && (
                        <button
                          type="button"
                          onClick={() => void handleStatus(subscription.subscription_id, "paused")}
                          disabled={updatingId === subscription.subscription_id}
                          className="rounded-lg border border-yellow-300 px-3 py-1.5 text-xs font-semibold text-yellow-700 hover:bg-yellow-50"
                        >
                          Pause
                        </button>
                      )}
                      {isAdmin && subscription.status === "paused" && (
                        <button
                          type="button"
                          onClick={() => void handleStatus(subscription.subscription_id, "active")}
                          disabled={updatingId === subscription.subscription_id}
                          className="rounded-lg border border-green-300 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-50"
                        >
                          Activate
                        </button>
                      )}
                      {isAdmin && subscription.status !== "revoked" && (
                        <button
                          type="button"
                          onClick={() => void handleStatus(subscription.subscription_id, "revoked")}
                          disabled={updatingId === subscription.subscription_id}
                          className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!subscriptions.length && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-500">
                    No webhook subscriptions yet. Add the ERP endpoint to start streaming compliance events.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3 md:flex-row md:items-center md:justify-between">
          <h2 className="font-semibold text-gray-900">Recent Deliveries</h2>
          <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            {(["all", "failed", "success"] as const).map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setDeliveryStatusFilter(status)}
                className={`rounded-md px-3 py-1 text-xs font-semibold capitalize ${
                  deliveryStatusFilter === status
                    ? "bg-white text-blue-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {deliveries.slice(0, 12).map((delivery) => (
            <div key={delivery.delivery_id} className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={delivery.status} />
                  <span className="text-sm font-medium text-gray-900">{delivery.event_type}</span>
                  {delivery.status_code && <span className="text-xs text-gray-500">HTTP {delivery.status_code}</span>}
                  <span className="text-xs text-gray-500">
                    {delivery.attempt_count}/{delivery.max_attempts || delivery.attempt_count} attempts
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {delivery.delivery_id} · {new Date(delivery.created_at).toLocaleString()}
                </p>
                {delivery.event_id && (
                  <p className="mt-1 text-xs text-gray-400">Event {delivery.event_id}</p>
                )}
                {delivery.attempts.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {delivery.attempts.map((attempt) => (
                      <span
                        key={`${delivery.delivery_id}-${attempt.attempt}`}
                        className={`rounded-full px-2 py-0.5 text-[11px] ${
                          attempt.status === "success"
                            ? "bg-green-50 text-green-700"
                            : "bg-red-50 text-red-700"
                        }`}
                      >
                        Try {attempt.attempt}: {attempt.status_code ? `HTTP ${attempt.status_code}` : attempt.status}
                      </span>
                    ))}
                  </div>
                )}
                {delivery.error && <p className="mt-1 text-xs text-red-600">{delivery.error}</p>}
              </div>
              <div className="flex flex-col items-start gap-2 md:items-end">
                <p className="break-all text-xs text-gray-400">{delivery.subscription_id}</p>
                {isAdmin && delivery.status === "failed" && (
                  <button
                    type="button"
                    onClick={() => void handleReplay(delivery.delivery_id)}
                    disabled={replayingId === delivery.delivery_id}
                    className="flex items-center gap-1 rounded-lg border border-blue-300 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                  >
                    {replayingId === delivery.delivery_id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                    Replay
                  </button>
                )}
              </div>
            </div>
          ))}
          {!deliveries.length && (
            <p className="px-4 py-10 text-center text-sm text-gray-500">
              No deliveries yet. Use Test on an active subscription to verify signing and endpoint reachability.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
